import { getNewHomeGrownBrands, getProductBrands } from '@api/brand';
import { getNewLaunchBrands, getNewLaunchProducts } from '@api/newlaunch';
import {
    getPopularProducts,
    getProductCategories,
    getTrendingProducts,
} from '@api/product';
import { getNearestStores, getServicability, getStays } from '@api/store';
import { Container, Section } from '@components';
import {
    BrandCard,
    CategoryCard,
    RichCard,
    RichCardV2,
    SmallStoreCard,
} from '@components/cards';
import { BrandProductCard } from '@components/cards/product/brand';
import { FeaturedStays, pickFeaturedStays } from '@components/home/featuredstays';
import { HomeHero } from '@components/home/hero';
import { CarouselContainer } from '@components/container/carousel';
import { Footer } from '@components/footer';
import { Header } from '@components/header';
import { TBrand, TProduct, TProductCategory, TServiceLocation, TStore } from '@components/types';
import appConfig from '@core/appconfig';
import { storeListJsonLd } from '@core/seo';
import { staticImageLoader } from '@core/utils';
import { ChevronRight, MapPin } from 'lucide-react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

const Navbar = dynamic(
  () => import('../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

interface Props {
  isMobile: boolean;
  productCategories: TProductCategory[];
  stays: TStore[];
  popularBrands: TBrand[];
  initialTrendingProducts: TProduct[];
  initialNewHomeGrownBrands: TBrand[];
  initialNewLaunchedBrands: TBrand[];
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const UA = context.req.headers['user-agent'] || '';
  const isMobile = Boolean(
    UA.match(
      /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
    )
  );
  const [
    productCategories,
    stays,
    popularBrands,
    initialNewHomeGrownBrands,
    initialNewLaunchedBrands,
    initialTrendingProducts,
  ] = await Promise.all([
    // ordering_on=popularity returns the platform's main categories (Food, Grocery, Electronics…).
    getProductCategories(null, true, 10, 'popularity'),
    getStays('', 0, 60).catch(() => [] as TStore[]),
    getProductBrands(),
    getNewHomeGrownBrands(),
    getNewLaunchBrands(),
    getTrendingProducts(),
    getNewLaunchProducts(),
  ]);

  return {
    props: {
      isMobile,
      productCategories,
      stays: stays || [],
      popularBrands,
      initialTrendingProducts,
      initialNewHomeGrownBrands,
      initialNewLaunchedBrands,
    },
  };
};

const Home: React.FC<Props> = ({
  isMobile,
  productCategories,
  stays,
  popularBrands,
  initialNewHomeGrownBrands,
  initialNewLaunchedBrands,
  initialTrendingProducts,
}: Props) => {
  const [popularProducts, setPopularProducts] = useState<TProduct[]>([]);
  const [trendingProducts, setTrendingProducts] = useState<TProduct[]>(
    initialTrendingProducts
  );
  const [nearestStores, setNearestStores] = useState<TStore[]>([]);
  const [newLaunchedProducts, setNewLaunchedProducts] = useState<TProduct[]>([]);
  const [newLaunchedBrands, setNewLaunchedBrands] = useState<TBrand[]>(
    initialNewLaunchedBrands
  );
  const [newHomeGrownBrands, setNewHomeGrownBrands] = useState<TBrand[]>(
    initialNewHomeGrownBrands
  );
  const [deliverableStores, setDeliverableStores] = useState([]);

  const getAllNearestStores = (extend: boolean = true) => {
    const res: Promise<TStore[]> = getNearestStores(null, 0, 10, appConfig?.accountId, false);
    res.then((result) => {
      setNearestStores(result);
    });
  };

  const getAllNewLaunchProducts = () => {
    const res: Promise<TProduct[]> = getNewLaunchProducts();
    res.then((result) => {
      setNewLaunchedProducts(result);
    });
  };

  const getAllNewLaunchBrands = () => {
    const res: Promise<TBrand[]> = getNewLaunchBrands();
    res.then((result) => {
      setNewLaunchedBrands(result);
    });
  };

  const fetchNewHomeGrownBrands = () => {
    const res: Promise<TBrand[]> = getNewHomeGrownBrands();
    res.then((result) => {
      setNewHomeGrownBrands(result);
    });
  };

  useEffect(() => {
    getPopularProducts().then((res) => {
      setPopularProducts(res);
    });

    getTrendingProducts().then((res) => {
      setTrendingProducts(res);
    });
    getAllNearestStores();
    getAllNewLaunchProducts();
    getAllNewLaunchBrands();
    fetchNewHomeGrownBrands();
  }, []);

  useEffect(() => {
    if (nearestStores?.length) {
      const userLocation = JSON.parse(localStorage.getItem("location"));
      getServicability(nearestStores?.map((s: TStore) => s.id), userLocation?.Location?.city_id, 0, nearestStores.length).then((res) => {
        let deliveryStores = res?.map((item: TServiceLocation) => item?.store_id)
        setDeliverableStores(deliveryStores)
      })

    }
  }, [nearestStores])

  const listJsonLd = useMemo(
    () => storeListJsonLd('Featured hotels, stays & stores', [
      { stores: pickFeaturedStays(stays), kind: 'stay' },
      { stores: nearestStores, kind: 'store' },
    ]),
    [stays, nearestStores]
  );

  return (
    <Container>
      <Head>
        <title>{`Online Shopping and Booking - ${appConfig.appName}`}</title>
        <meta
          name="keywords"
          content={`Local Stores,Local+Online,Online Stores,Shopping Sites,Local Services, Online Booking,Online Shopping,${appConfig.appName}`}
        />
        <meta
          name="description"
          content={`Discover products and services around me whether local or online. Find Stores and Product on ${appConfig.appName} an online platform which provides product visiblity, best price discovering and all available deals and offers.`}
        />
        <meta property="og:title" content={`${appConfig.appHome} - ${appConfig.appName}`} />
        <meta
          property="og:description"
          content={`Discover products and services around you whether local or online. ${appConfig.appName} provides product visiblity, best price discovering and all available deals and offers.`}
        />
        <meta
          name="og:image"
          content="https://cdn.jhattse.com/public/consumer/square-logo.png"
        />
        <meta property="og:url" content="https://jhattse.com" />
        <link rel="canonical" href="https://jhattse.com" />
        {listJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(listJsonLd) }}
          />
        )}
      </Head>
      <div className="flex justify-center">
        <Header />
      </div>
      <div className="flex flex-col gap-8 mx-auto w-full max-w-[1240px] pt-4 pb-10 lt-sm:pb-20">
        {/* Hotels first, local stores second. Branded storefronts (e.g. GetMyDish) keep their banners. */}
        {appConfig?.branded ? (
        <div className="flex flex-col justify-center gap-4 px-2">
          <div className="section select-none text-neutral-900">
            <CarouselContainer>
              <SwiperSlide style={{ width: 'auto' }} className="min-w-0">
                <div className="group relative shrink-0 md:w-[400px] w-[300px] md:h-[216px] h-[162px] rounded-2xl overflow-hidden shadow-sm">
                  <Link href="/stays" prefetch={false}>
                    <Image
                      loader={staticImageLoader}
                      src="public/consumer/stay.webp"
                      width={isMobile ? '300' : '400'}
                      height={isMobile ? '162' : '216'}
                      className="flex items-center justify-center md:w-[400px] w-[300px] md:h-[216px] h-[162px] object-cover group-hover:scale-105 transition-transform duration-300"
                      alt={`Book your stay with ${appConfig?.appName}`}
                      priority={true}
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-3">
                      <span className="text-white font-manrope font-bold text-sm md:text-base">Book your stay</span>
                    </div>
                  </Link>
                </div>
              </SwiperSlide>
              <SwiperSlide style={{ width: 'auto' }} className="min-w-0">
                <div className="group relative shrink-0 md:w-[400px] w-[300px] md:h-[216px] h-[162px] rounded-2xl overflow-hidden shadow-sm">
                  <Link href="/category/2819/food" prefetch={false}>
                    <Image
                      loader={staticImageLoader}
                      src="public/consumer/food_banner.webp"
                      width={isMobile ? '300' : '400'}
                      height={isMobile ? '162' : '216'}
                      className="flex items-center justify-center md:w-[400px] w-[300px] md:h-[216px] h-[162px] object-cover group-hover:scale-105 transition-transform duration-300"
                      alt={`Food Restaurants on ${appConfig?.appName}`}
                      priority={true}
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-3">
                      <span className="text-white font-manrope font-bold text-sm md:text-base">Order food nearby</span>
                    </div>
                  </Link>
                </div>
              </SwiperSlide>
              <SwiperSlide style={{ width: 'auto' }} className="min-w-0">
                <div className="group relative shrink-0 md:w-[400px] w-[300px] md:h-[216px] h-[162px] rounded-2xl overflow-hidden shadow-sm">
                  <Link href="/stores" prefetch={false}>
                    <Image
                      loader={staticImageLoader}
                      width={isMobile ? '300' : '400'}
                      height={isMobile ? '162' : '216'}
                      className="flex items-center justify-center md:w-[400px] w-[300px] md:h-[216px] h-[162px] object-cover group-hover:scale-105 transition-transform duration-300"
                      src="public/consumer/store_banner.webp"
                      alt={`Local Stores on ${appConfig?.appName}`}
                      priority={true}
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-3">
                      <span className="text-white font-manrope font-bold text-sm md:text-base">Explore local stores</span>
                    </div>
                  </Link>
                </div>
              </SwiperSlide>
            </CarouselContainer>
          </div>
        </div>
        ) : (
          <>
            <HomeHero stays={stays} categories={productCategories} />
            <FeaturedStays stays={pickFeaturedStays(stays)} />
          </>
        )}
        <div className="px-2">
          <div className="bg-white border border-neutral-200/70 shadow-sm rounded-2xl flex flex-col gap-2 p-4">
            <h2 className="text-lg font-bold font-manrope text-neutral-900 flex items-center gap-1.5">
              <MapPin size={18} className="text-brand-500" />
              Nearest to You
            </h2>
            <Suspense>
              <Swiper spaceBetween={10} // Space between slides
                slidesPerView="auto" // Number of slides visible
                loop={true} // Infinite loop
                autoplay={{
                  delay: 2000, // Autoplay interval
                  disableOnInteraction: true,
                  pauseOnMouseEnter: true
                }}
                modules={[Autoplay]}
                className={`mySwiper w-full overflow-x-scroll no-scrollbar`}
              >
                {nearestStores?.map((store, index: number) => (
                  <SwiperSlide
                    style={{ width: 'auto' }}
                    className="min-w-0"
                    key={index}
                  >
                    <SmallStoreCard store={store} isAvailable={deliverableStores.includes(store.id)} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Suspense>
          </div>
        </div>
        {appConfig?.showPopularCategories && <Suspense>
          <Section
            title="Popular Categories"
            viewUrl="/category/"
            products={productCategories}
            isCarousel={true}
            element={CategoryCard}
            intent="productcategory"
            style="p-2 text-neutral-900"
            headStyle="md:text-xl text-lg py-1 font-bold font-manrope text-neutral-900"
            loading="lazy"
            isMarquee={true}
          />
        </Suspense>}
        {appConfig?.showNewLaunchedBrands && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-2">
          <div className="flex flex-col gap-2 justify-between w-full">
            <div
              className="rounded-2xl pl-4 py-3 pr-0 shadow-sm"
              style={{ background: 'linear-gradient(120deg, #0F6E56 0%, #0B5D57 60%, #093F32 100%)' }}
            >
              <Suspense>
                <Section
                  title="Newly Launched Brands"
                  products={newLaunchedBrands}
                  element={BrandCard}
                  isCarousel={true}
                  style="pb-2 text-neutral-100"
                  headStyle="text-lg py-1 font-bold font-manrope text-white"
                  loading="lazy"
                  isMarquee={true}
                />
              </Suspense>
              <Link href="/brand/explore?ordering_on=new" prefetch={false}>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm text-white/90 font-manrope font-semibold cursor-pointer hover:text-white pr-4 pt-1"
                >
                  VIEW ALL <ChevronRight size={14} />
                </button>
              </Link>
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-between w-full">
            <div className="bg-white border border-neutral-200/70 shadow-sm rounded-2xl pl-4 py-3 pr-0">
              <Suspense>
                <Section
                  title="Homegrown Brands"
                  products={newHomeGrownBrands}
                  element={BrandCard}
                  isCarousel={true}
                  style="pb-2 text-neutral-900"
                  headStyle="text-lg py-1 font-bold font-manrope text-neutral-900"
                />
              </Suspense>
              <Link href={'/brand/homegrown'} prefetch={false}>
                <div className="flex items-center font-manrope pr-4 pt-1">
                  <button className="flex items-center gap-1 font-semibold text-sm text-business-500 font-manrope cursor-pointer hover:underline">
                    VIEW ALL <ChevronRight size={14} />
                  </button>
                </div>
              </Link>
            </div>
          </div>
        </div>}
        {newLaunchedProducts?.length > 0 &&
          <div className="flex flex-col gap-2 justify-between px-2">
            <div className="bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-4">
              <Suspense>
                <Section
                  title="New Products"
                  products={newLaunchedProducts}
                  element={RichCard}
                  isCarousel={true}
                  style="pb-2 text-neutral-900"
                  headStyle="md:text-xl text-lg py-1 font-bold font-manrope text-neutral-900"
                  loading="lazy"
                />
              </Suspense>
              <Link href={'/product/trending'} prefetch={false}>
                <div className="w-full flex items-center font-manrope">
                  <button className="flex items-center gap-1 font-semibold md:text-base text-sm text-business-500 font-manrope cursor-pointer hover:underline">
                    VIEW ALL <ChevronRight size={14} />
                  </button>
                </div>
              </Link>
            </div>
          </div>}
        {appConfig?.showTrendingProducts && <Suspense>
          <Section
            title="Trending Products"
            products={
              trendingProducts?.length > 0 ? trendingProducts : popularProducts
            }
            element={RichCardV2}
            isCarousel={true}
            style="p-2 text-neutral-900"
            headStyle="text-lg py-1 font-bold font-manrope text-neutral-900"
            loading="lazy"
          />
        </Suspense>}
        {appConfig?.showPopularBrands && <Section
          title="Explore Brands"
          viewUrl="/brand/explore"
          products={popularBrands}
          element={BrandCard}
          isCarousel={true}
          style="p-2 text-neutral-900"
          headStyle="md:text-xl text-lg py-1 font-bold font-manrope text-neutral-900"
          loading="lazy"
          isMarquee={true}
        />}
        {appConfig?.showPopularProducts && <Suspense>
          <Section
            title="Popular Products"
            viewUrl="/product/trending"
            products={popularProducts}
            element={BrandProductCard}
            isCarousel={true}
            style="p-2 text-neutral-900"
            headStyle="md:text-xl text-lg py-1 font-bold font-manrope text-neutral-900"
            loading="lazy"
            isAvailable={true}
          />
        </Suspense>}
        {!isMobile && !appConfig?.branded ? (
          <div className="px-2 relative hidden lg:block">
            <Link
              className="w-full h-full relative block rounded-2xl overflow-hidden shadow-sm"
              href="https://business.jhattse.com/products/billing-software-for-brands"
              prefetch={false}
            >
              <Image
                loader={staticImageLoader}
                className="w-full h-full object-contain"
                src="public/consumer/business_banner.webp"
                width="1544"
                height="268"
                loading="lazy"
                alt={`Start selling on ${appConfig?.appName}`}
                quality={100}
              />
            </Link>
          </div>
        ) : !appConfig?.branded ? (
          <div className="rounded-2xl relative px-2 md:hidden flex overflow-hidden">
            <Link
              href="https://business.jhattse.com/products/billing-software-for-brands"
              target="_blank"
              prefetch={false}
              className="w-full rounded-2xl overflow-hidden shadow-sm"
            >
              <Image
                loader={staticImageLoader}
                className="h-full w-full object-contain"
                src="public/consumer/business_banner_mobile.webp"
                width="368"
                height="220"
                loading="lazy"
                alt={`Start selling on ${appConfig?.appName}`}
                quality={100}
              />
            </Link>
          </div>
        ) : <></>}
      </div>
      <Navbar />
      <Footer />
    </Container>
  );
};

export default Home;
